import CompensationProfileForm from "@/components/Forms/CompensationProfileForm";
import Button from "@/components/Button";
import { GET_COMPENSATION_PROFILES_QUERY } from "@/components/Forms/CompensationProfileForm/queries";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export default function CreateCompensationProfilePage() {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageCompensationProfiles
  });

  const { pgaId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const historicalSnapshotRequested = searchParams.get("historical") === "true";
  const earlierProfileId = searchParams.get("from");
  const laterProfileId = searchParams.get("to");

  const { data, loading } = useQuery(GET_COMPENSATION_PROFILES_QUERY, {
    variables: { pgaId: pgaId }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-lg font-medium">Loading compensation profile data...</p>
        </div>
      </div>
    );
  }

  const compensationProfiles = data?.person?.compensationProfiles ?? [];
  const earlierProfile = compensationProfiles.find((profile: any) => profile.id === earlierProfileId);
  const laterProfile = compensationProfiles.find((profile: any) => profile.id === laterProfileId);
  const historicalSnapshot =
    historicalSnapshotRequested && earlierProfile && laterProfile
      ? {
          defaultValues: earlierProfile,
          minUpdatedAt: earlierProfile.updatedAt,
          maxUpdatedAt: laterProfile.updatedAt
        }
      : undefined;

  if (historicalSnapshotRequested && !historicalSnapshot) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md">
          <p className="text-lg font-medium mb-2">Unable to create historical compensation profile.</p>
          <p className="text-body-medium text-sys-brand-on-surface-variant mb-6">
            The selected profile placement could not be found. Return to compensation history and choose the placement
            again.
          </p>
          <Button variant="filled" onClick={() => navigate(`/people/${pgaId}/career/compensation`)}>
            Back to Compensation Profiles
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CompensationProfileForm
      onSubmit={() => navigate(`/people/${pgaId}/career/compensation`)}
      pgaId={pgaId}
      contactId={data?.person?.id}
      defaultValues={historicalSnapshot?.defaultValues as any}
      historicalSnapshotConfig={
        historicalSnapshot
          ? {
              minUpdatedAt: historicalSnapshot.minUpdatedAt,
              maxUpdatedAt: historicalSnapshot.maxUpdatedAt
            }
          : undefined
      }
      includeInitialTotalCompensation={compensationProfiles.length === 0}
    />
  );
}
